import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { useClientInfo, useAppContext } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import MyDatePicker from "../components/MyDatePicker";
import { addPdfFooter } from "../utils/reportFooter";
import { formatDate, resolveDateRange, printPdfDoc } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* ===============================
   COLUMNS (SINGLE SOURCE)
================================ */
const BOOKING_COLUMNS = [
  { key: "reporting_date", label: "Reporting Date" },
  { key: "booking_no", label: "Booking No" },
  { key: "customername", label: "Customer Name" },
  { key: "duty_type", label: "Duty Type" },
  { key: "vehicle_group", label: "Group" },
  { key: "vehicle_number", label: "Vehicle No" },
  { key: "driver_name", label: "Driver" },
  { key: "release_date", label: "Release Date" },
  { key: "usage_days", label: "Days" },
  { key: "advance_amount", label: "Advance" },
  { key: "payment_mode", label: "Payment Mode" },
  { key: "booking_status", label: "Status" },
  { key: "tripsheet_number", label: "Tripsheet No" },
];

const DATE_KEYS = ["reporting_date", "release_date"];

const formatCell = (key, r) =>
  DATE_KEYS.includes(key) ? formatDate(r[key]) : r[key] ?? "";

export default function BookingReport() {
  const navigate = useNavigate();

  const { clientId, client } = useClientInfo();
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // display label e.g. "2025-26"

  const [rows, setRows] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [dutyType, setDutyType] = useState("");
  const [search, setSearch] = useState("");

  /* ===============================
     BACK BUTTON -> always Reports dashboard
  ================================ */
  const handleBack = () => navigate(REPORTS_PATH);

  /* ===============================
     LOAD REPORT (FY aware)
  ================================ */
  const loadReport = async ({ ignoreDates = false } = {}) => {
    try {
      const { from, to } = resolveDateRange(selectedFinancialYear, fromDate, toDate, ignoreDates);

      const res = await axios.get("/api/bookings/report", {
        params: {
          client_id: clientId,
          from_date: from,
          to_date: to,
          fy_id: selectedFinancialYear?.fyid || "",
          fy, // lets the backend filter too, if it supports it
          status,
          duty_type: dutyType,
          search,
        },
      });

      setRows(res.data || []);
    } catch (err) {
      console.error("Failed to load bookings report:", err);
      setRows([]);
    }
  };

  // Client or FY changed: clear manual dates and load the whole FY
  useEffect(() => {
    if (!clientId || financialYearLoading) return;
    setFromDate("");
    setToDate("");
    loadReport({ ignoreDates: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading]);

  /* ===============================
     PDF GENERATOR
  ================================ */
  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headerEndY = drawClientHeader(doc, client);

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text(`Bookings Report${fy ? ` (FY ${fy})` : ""}`, 14, headerEndY);

    const columns = BOOKING_COLUMNS.map((c) => c.label);
    const body = rows.map((r) =>
      BOOKING_COLUMNS.map((c) => formatCell(c.key, r)),
    );

    autoTable(doc, {
      head: [columns],
      body,
      startY: headerEndY + 6,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    // FOOTER (ALWAYS LAST)
    addPdfFooter(doc);

    return doc;
  };

  /* ===============================
     PRINT / PDF / EXCEL
  ================================ */
  const handlePrint = () => printPdfDoc(generatePDF());

  const handlePDF = () => {
    generatePDF().save(`Bookings_Report${fy ? `_${fy}` : ""}.pdf`);
  };

  const handleExcel = () => {
    const data = rows.map((r) => {
      const obj = {};
      BOOKING_COLUMNS.forEach((c) => {
        obj[c.label] = formatCell(c.key, r);
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, `Bookings_Report${fy ? `_${fy}` : ""}.xlsx`);
  };

  /* ===============================
     UI
  ================================ */
  return (
    <Paper sx={{ p: 3 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Bookings
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Bookings Report
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <MyDatePicker
          label="From Date"
          value={fromDate}
          onChange={setFromDate}
        />

        <MyDatePicker label="To Date" value={toDate} onChange={setToDate} />

        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 110 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Confirmed">Confirmed</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Duty Type"
          value={dutyType}
          onChange={(e) => setDutyType(e.target.value)}
          sx={{ minWidth: 160 }}
          InputProps={{ sx: { fontSize: 14 } }}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Local">Local</MenuItem>
          <MenuItem value="Outstation">Outstation</MenuItem>
          <MenuItem value="EmployeeTransfer">Employee Transfer</MenuItem>
          <MenuItem value="AirportTransfer">Airport Transfer</MenuItem>
          <MenuItem value="Contract">Monthly Contract</MenuItem>
          <MenuItem value="Package">Package</MenuItem>
          <MenuItem value="OSDayRate">OS Day Rate</MenuItem>
          <MenuItem value="KmSlab">Km Slab</MenuItem>
        </TextField>

        <TextField
          size="small"
          label="Search Booking / Customer / Vehicle / Driver"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          size="small"
          sx={{ height: 40, minWidth: 80 }}
          onClick={() => loadReport()}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          size="small"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handlePrint}
        >
          Print
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handlePDF}
        >
          PDF
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="success"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handleExcel}
        >
          Excel
        </Button>
      </Box>

      {/* TABLE */}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead sx={{ background: "#f1f5f9" }}>
            <TableRow>
              {BOOKING_COLUMNS.map((c) => (
                <TableCell key={c.key} sx={{ fontSize: 11, fontWeight: 700 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={r.bookingid ?? idx}>
                {BOOKING_COLUMNS.map((c) => (
                  <TableCell key={c.key} sx={{ fontSize: 11 }}>
                    {formatCell(c.key, r)}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={BOOKING_COLUMNS.length} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
